import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  TwitterShareButton,
  FacebookShareButton,
  EmailShareButton,
  RedditShareButton,
  LinkedinShareButton,
  XIcon,
  FacebookIcon,
  EmailIcon,
  RedditIcon,
  LinkedinIcon
} from 'react-share';
import Header from '../components/Header';
import SpaceBackground from '../components/SpaceBackground';
import Footer from '../components/Footer';
import ChapterNavigation from '../components/ChapterNavigation';
import ComingSoon from '../components/ComingSoon';
import { AdIcons } from '../components/Ad';
import SienBanner from '../components/SienBanner';
import BoothProduct from '../components/BoothProduct';
import './BibleGal.css';
import './BibleReading.css';
import './ChineseTheme.css';
import formatBibleReading from '../utils/formatBibleReading';
import readingText from '../utils/readingText';
import { GAL_EDITIONS } from '../data/galEditions';
import HEBREW_EDITIONS from '../data/hebrewEditions.json';
import GREEK_EDITIONS from '../data/greekEditions.json';
import LATIN_EDITIONS from '../data/latinEditions.json';
import CHINESE_EDITIONS from '../data/chineseEditions.json';
import KIRIKAN2_ARTWORK from '../data/kirikan2Artwork.json';
import useReadingPosition from '../utils/useReadingPosition';
import prepareReadingChapters from '../utils/prepareReadingChapters';
import useReadingChapters from '../utils/useReadingChapters';
import ReformationCommentary from '../components/ReformationCommentary';
import BibleSocial from '../components/BibleSocial';
import { addCommentaryButtons, commentaryVerseId } from '../utils/reformationCommentary';

// Only reviewed chapters are shared with norm/ruby as generation progresses.
const SHARED_ARTWORK_BOOKS = new Set(Object.keys(KIRIKAN2_ARTWORK).map(id => id.replace(/-\d+$/, '')));

const NEW_TESTAMENT_BOOKS = new Set([
  'matthew', 'mark', 'luke', 'john', 'acts', 'romans',
  '1corinthians', '2corinthians', 'galatians', 'ephesians', 'philippians', 'colossians',
  '1thessalonians', '2thessalonians', '1timothy', '2timothy', 'titus', 'philemon',
  'hebrews', 'james', '1peter', '2peter', '1john', '2john', '3john', 'jude', 'revelation',
]);
const APOCRYPHA_BOOKS = new Set([
  '1esdras', '2esdras', 'tobit', 'judith', 'esther-additions', 'wisdom', 'sirach',
  'baruch', 'letter-of-jeremiah', 'song-of-three', 'susanna', 'bel-and-dragon',
  'prayer-of-manasseh', '1maccabees', '2maccabees',
]);

// Custom Blogger Share Button
const BloggerShareButton = ({ url, title, text, image, children }) => {
  // Clean text: remove HTML tags and markdown syntax
  let cleanText = text || '';

  // Remove HTML tags
  cleanText = cleanText.replace(/<[^>]*>/g, '');

  // Remove markdown image syntax
  cleanText = cleanText.replace(/!\[.*?\]\(.*?\)/g, '');

  // Remove markdown headers
  cleanText = cleanText.replace(/^#{1,6}\s+/gm, '');

  // Remove markdown bold/italic
  cleanText = cleanText.replace(/[*_]{1,2}([^*_]+)[*_]{1,2}/g, '$1');

  // Remove markdown links but keep text
  cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Normalize whitespace
  cleanText = cleanText.replace(/\s+/g, ' ').trim();

  // Truncate to 1000 chars to avoid URL length issues
  const truncatedText = cleanText.length > 1000 ? cleanText.substring(0, 997) + '...' : cleanText;

  // Build HTML content: image + text + link at bottom
  const imageTag = image ? `<img src="${image}" alt="${title}" style="max-width:100%;height:auto;"><br><br>` : '';
  const content = `${imageTag}${truncatedText}<br><br><a href="${url}">${url}</a>`;

  const bloggerUrl = `https://www.blogger.com/blog-this.g?u=${encodeURIComponent(url)}&n=${encodeURIComponent(title)}&t=${encodeURIComponent(content)}`;

  return (
    <a
      href={bloggerUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'inline-block', cursor: 'pointer' }}
    >
      {children}
    </a>
  );
};

// Custom Blogger Icon
const BloggerIcon = ({ size = 32 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ display: 'block' }}
  >
    <rect width="24" height="24" fill="#FF5722" />
    <path
      d="M10.5 9h3c.28 0 .5.22.5.5s-.22.5-.5.5h-3c-.28 0-.5-.22-.5-.5s.22-.5.5-.5zm0 5h3c.28 0 .5.22.5.5s-.22.5-.5.5h-3c-.28 0-.5-.22-.5-.5s.22-.5.5-.5zm6.5-7h-2.5V5c0-1.1-.9-2-2-2h-4C7.9 3 7 3.9 7 5v4c0 1.1.9 2 2 2v1c0 1.1.9 2 2 2v3c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z"
      fill="white"
    />
  </svg>
);

function Bible() {
  const { section, chapter, verse } = useParams();
  const testament = NEW_TESTAMENT_BOOKS.has(section) ? 'new' : APOCRYPHA_BOOKS.has(section) ? 'apocrypha' : 'old';
  const [searchParams] = useSearchParams();
  const [content, setContent] = useState('');
  const [loadedContentKey, setLoadedContentKey] = useState('');
  const readingContainerRef = useRef(null);
  const [currentChapter, setCurrentChapter] = useState('');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [metaData, setMetaData] = useState({
    title: '',
    description: '',
    url: '',
    image: ''
  });
  const [sharePopup, setSharePopup] = useState({
    show: false,
    title: '',
    text: '',
    url: '',
    x: 0,
    y: 0,
    preview: '' // For showing selected verses
  });
  const [hasScrolledToVerse, setHasScrolledToVerse] = useState(false);
  const [reformationVerse, setReformationVerse] = useState(null);

  // Reset scroll flag when URL changes
  useEffect(() => {
    setHasScrolledToVerse(false);
  }, [section, chapter, verse]);

  // Japanese book names mapping
  const bookNamesJa = useMemo(() => ({
    genesis: '創世記',
    exodus: '出エジプト記',
    leviticus: 'レビ記',
    numbers: '民数記',
    deuteronomy: '申命記',
    joshua: 'ヨシュア記',
    judges: '士師記',
    ruth: 'ルツ記',
    '1samuel': 'サムエル記上',
    '2samuel': 'サムエル記下',
    '1kings': '列王記上',
    '2kings': '列王記下',
    '1chronicles': '歴代志上',
    '2chronicles': '歴代志下',
    ezra: 'エズラ記',
    nehemiah: 'ネヘミヤ記',
    esther: 'エステル記',
    job: 'ヨブ記',
    psalms: '詩篇',
    proverbs: '箴言',
    ecclesiastes: '伝道の書',
    songofsongs: '雅歌',
    isaiah: 'イザヤ書',
    jeremiah: 'エレミヤ書',
    lamentations: '哀歌',
    ezekiel: 'エゼキエル書',
    daniel: 'ダニエル書',
    hosea: 'ホセア書',
    joel: 'ヨエル書',
    amos: 'アモス書',
    obadiah: 'オバデヤ書',
    jonah: 'ヨナ書',
    micah: 'ミカ書',
    nahum: 'ナホム書',
    habakkuk: 'ハバクク書',
    zephaniah: 'ゼパニヤ書',
    haggai: 'ハガイ書',
    zechariah: 'ゼカリヤ書',
    zecariah: 'ゼカリヤ書',
    malachi: 'マラキ書',
    matthew: 'マタイによる福音書',
    mark: 'マルコによる福音書',
    luke: 'ルカによる福音書',
    john: 'ヨハネによる福音書',
    acts: '使徒行伝',
    romans: 'ローマ人への手紙',
    '1corinthians': 'コリント人への第一の手紙',
    '2corinthians': 'コリント人への第二の手紙',
    galatians: 'ガラテヤ人への手紙',
    ephesians: 'エペソ人への手紙',
    philippians: 'ピリピ人への手紙',
    colossians: 'コロサイ人への手紙',
    '1thessalonians': 'テサロニケ人への第一の手紙',
    '2thessalonians': 'テサロニケ人への第二の手紙',
    '1timothy': 'テモテへの第一の手紙',
    '2timothy': 'テモテへの第二の手紙',
    titus: 'テトスへの手紙',
    philemon: 'ピレモンへの手紙',
    hebrews: 'ヘブル人への手紙',
    james: 'ヤコブの手紙',
    '1peter': 'ペテロの第一の手紙',
    '2peter': 'ペテロの第二の手紙',
    '1john': 'ヨハネの第一の手紙',
    '2john': 'ヨハネの第二の手紙',
    '3john': 'ヨハネの第三の手紙',
    jude: 'ユダの手紙',
    revelation: 'ヨハネの黙示録',
    // Deuterocanonical books
    tobit: 'トビト記',
    judith: 'ユディト記',
    wisdom: '知恵の書',
    sirach: 'シラ書',
    baruch: 'バルク書',
    'letter-of-jeremiah': 'エレミヤの書翰',
    'song-of-three': '三童児の歌',
    '1maccabees': 'マカビー第一書',
    '2maccabees': 'マカバイ記二',
    susanna: 'スザンナ',
    'bel-and-dragon': 'ベルと龍',
    'prayer-of-manasseh': 'マナセの祈禱',
  }), []);

  useEffect(() => {
    if (chapter) {
      setCurrentChapter(chapter);
    } else {
      setCurrentChapter('1');
    }
  }, [section, chapter]);

  const typeParam = searchParams.get('type');
  const isSocial = searchParams.get('view') === 'sns';
  const allowedTypes = useMemo(() => (
    [
      'norm', 'ruby', 'en',
      ...(Object.prototype.hasOwnProperty.call(GAL_EDITIONS, section) ? ['gal'] : []),
      ...(Object.prototype.hasOwnProperty.call(HEBREW_EDITIONS, section) ? ['hebrew'] : []),
      ...(Object.prototype.hasOwnProperty.call(GREEK_EDITIONS, section) ? ['greek'] : []),
      ...(Object.prototype.hasOwnProperty.call(LATIN_EDITIONS, section) ? ['latin'] : []),
      ...(Object.prototype.hasOwnProperty.call(CHINESE_EDITIONS, section) ? ['zh'] : []),
    ]
  ), [section]);
  const currentType = allowedTypes.includes(typeParam) ? typeParam : 'norm';
  // These editions share the chapter/verse numbering used by the source index.
  const supportsReformationCommentary = ['norm', 'ruby', 'gal', 'en'].includes(currentType) && !APOCRYPHA_BOOKS.has(section);
  const galEdition = Object.prototype.hasOwnProperty.call(GAL_EDITIONS, section) ? GAL_EDITIONS[section] : null;
  const isGalEdition = Boolean(galEdition) && currentType === 'gal';
  const readingContent = useMemo(() => {
    if (!content) return content;
    const isStandardEdition = ['norm', 'ruby'].includes(currentType);
    const formatted = isStandardEdition ? formatBibleReading(content) : content;
    const hasSharedArtwork = isStandardEdition && SHARED_ARTWORK_BOOKS.has(section);
    const prepare = (html) => {
      const prepared = prepareReadingChapters(html, {
        gold: !isGalEdition && !['ruby', 'zh'].includes(currentType),
        hebrew: currentType === 'hebrew',
      });
      return supportsReformationCommentary ? addCommentaryButtons(prepared, currentType === 'en' ? 'en' : 'ja') : prepared;
    };
    if (!galEdition || (!isGalEdition && !hasSharedArtwork)) return prepare(formatted);

    const doc = new DOMParser().parseFromString(formatted, 'text/html');
    if (isGalEdition) doc.querySelectorAll('em[id]').forEach((marker) => {
      const verseText = marker.nextElementSibling;
      if (!verseText?.matches('span[id="verse"]')) return;

      const verseBlock = doc.createElement('div');
      verseBlock.className = 'gal-verse';
      marker.before(verseBlock);
      verseBlock.append(marker, verseText);
    });

    const chapters = Array.from(doc.querySelectorAll('div[id]'))
      .filter((element) => /^\d+$/.test(element.id) && element.querySelector('h3'));

    chapters.slice(1).forEach((chapterElement, index) => {
      const previousChapter = Number(chapters[index].id);
      const artwork = galEdition.illustrations.find(({ chapter }) => chapter === previousChapter);
      const artworkVersion = KIRIKAN2_ARTWORK[`${section}-${previousChapter}`];
      if (!artwork || (isStandardEdition && !artworkVersion)) return;

      const illustration = doc.createElement('figure');
      illustration.className = isGalEdition ? 'gal-chapter-illustration' : 'bible-chapter-illustration';
      const img = doc.createElement('img');
      img.src = `${process.env.PUBLIC_URL}/static/images/${galEdition.images}/${artwork.file}?v=${artworkVersion || galEdition.version}`;
      img.alt = `${galEdition.name} 第${previousChapter}章「${artwork.title}」をイメージしたギャル姿のきりかん`;
      img.width = 1120;
      img.height = 747;
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      illustration.appendChild(img);
      const caption = doc.createElement('figcaption');
      caption.textContent = `第${previousChapter}章 · ${artwork.title}`;
      illustration.appendChild(caption);
      chapterElement.before(illustration);
    });

    return prepare(doc.body.innerHTML);
  }, [content, currentType, isGalEdition, galEdition, section, supportsReformationCommentary]);
  useEffect(() => { setReformationVerse(null); }, [section, currentType, chapter]);

  const openReformationCommentary = (event) => {
    if (!supportsReformationCommentary || event.target.closest('.reading-job-link')) return;
    const isButton = event.target.closest('[data-commentary-verse]');
    if (event.type === 'click' && !isButton) return;
    const id = commentaryVerseId(event.target);
    if (!id) return;
    event.preventDefault();
    event.stopPropagation();
    const [selectedChapter, selectedVerse] = id.split('-').map(Number);
    const block = event.target.closest('.bible-reading-verse, .gal-verse, .hebrew-verse, tr');
    const copy = block?.cloneNode(true);
    copy?.querySelectorAll('button, em, td.vn, rt, rp, .reading-job-link').forEach(node => node.remove());
    const fallback = event.target.closest('span[id="verse"]');
    setSharePopup(current => ({ ...current, show: false }));
    setReformationVerse({ chapter: selectedChapter, verse: selectedVerse, verseText: copy?.textContent.trim() || fallback?.textContent.trim() || '' });
  };
  useReadingChapters(readingContainerRef, isSocial ? '' : readingContent);
  useReadingPosition({
    containerRef: readingContainerRef,
    ready: !isSocial && loadedContentKey === `${section}:${currentType}` && Boolean(content) && !showComingSoon,
    content: readingContent,
    section,
    type: currentType,
    bookName: bookNamesJa[section] || section,
  });

  useEffect(() => {
    if (isSocial) return;
    const type = currentType;
    const controller = new AbortController();

    setShowComingSoon(false);
    setLoadedContentKey('');
    fetch(`${process.env.PUBLIC_URL}/static/html/${type}/${section}.htm`, { signal: controller.signal })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then(data => {
        if (controller.signal.aborted) return;
        setContent(data);
        setLoadedContentKey(`${section}:${type}`);
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        console.error('Error loading the HTML content:', error);
        setShowComingSoon(true);
        setContent('');
      });
    return () => controller.abort();
  }, [section, currentType, isSocial]);

  // Update metadata based on URL parameters (for specific verse)
  useEffect(() => {
    if (isSocial) {
      setMetaData({
        title: '聖書全体 · SNS - ASMRキリスト教会',
        description: '聖句、スレッド、註解抜粋のQTで読む聖書。',
        url: `https://www.asmrchurch.com/bible/${section}?type=${currentType}&view=sns`,
        image: 'https://www.asmrchurch.com/static/images/card1.jpg',
      });
      return;
    }

    if (!content) return;

    // Remove previous highlights
    const previousHighlights = document.querySelectorAll('.verse-highlight');
    previousHighlights.forEach(el => el.classList.remove('verse-highlight'));

    if (chapter && verse) {
      // URL has chapter and verse like /bible/genesis/1/1 or /bible/genesis/1/1?end=3
      const endVerse = searchParams.get('end');
      const startVerseNum = parseInt(verse);
      const endVerseNum = endVerse ? parseInt(endVerse) : startVerseNum;

      // Collect all verse texts for metadata
      let allVerseTexts = [];
      const collectedVerseElements = new Set();

      // Highlight all verses in range
      for (let v = startVerseNum; v <= endVerseNum; v++) {
        const verseId = `${chapter}-${v}`;
        const verseElement = document.getElementById(verseId);

        if (verseElement) {
          const nextElement = (verseElement.closest('em') || verseElement).nextElementSibling;

          // Highlight the verse
          verseElement.classList.add('verse-highlight');
          if (nextElement) {
            nextElement.classList.add('verse-highlight');

            // Collect verse text
            const verseSpans = nextElement.querySelectorAll('span[id="verse"]');
            const verseText = verseSpans.length > 0
              ? readingText(verseSpans[verseSpans.length - 1])
              : readingText(nextElement);
            if (!collectedVerseElements.has(nextElement)) {
              allVerseTexts.push(verseText);
              collectedVerseElements.add(nextElement);
            }
          }

          // Scroll to the first verse only once when first loaded
          if (v === startVerseNum && !hasScrolledToVerse) {
            setTimeout(() => {
              const bannerHeight = document.querySelector('.ios-app-banner')?.getBoundingClientRect().height || 0;
              const yOffset = -150 - bannerHeight;
              const y = verseElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
              // A shared verse can be hundreds of screens into a book. Jump directly
              // instead of painting all intervening chapters and illustrations.
              window.scrollTo({ top: y, behavior: 'instant' });
            }, 100);
            setHasScrolledToVerse(true);
          }
        }
      }

      // Set metadata
      const bookNameJa = bookNamesJa[section] || section;
      const verseRef = endVerse
        ? `${bookNameJa} ${chapter}:${verse}-${endVerse}`
        : `${bookNameJa} ${chapter}:${verse}`;
      const combinedText = allVerseTexts.join(' ');

      setMetaData({
        title: verseRef,
        description: combinedText || '口語訳聖書 旧約：1955年版・新約：1954年版',
        url: `https://www.asmrchurch.com/bible/${section}/${chapter}/${verse}?${new URLSearchParams({ type: currentType, ...(endVerse ? { end: endVerse } : {}) })}`,
        image: 'https://www.asmrchurch.com/static/images/card1.jpg'
      });

      return;
    }

    // Default metadata for the book
    const bookNameJa = bookNamesJa[section] || section;
    setMetaData({
      title: `${bookNameJa}${currentType === 'hebrew' ? ' ヘブライ語原文' : currentType === 'greek' ? ' ギリシャ語原文' : currentType === 'latin' ? ' ラテン語訳' : currentType === 'zh' ? ' 中文・和合本' : ''} - ASMRキリスト教会`,
      description: currentType === 'hebrew'
        ? `${bookNameJa} 全${HEBREW_EDITIONS[section].chapters}章 原語本文（ヘブライ語・一部アラム語、Unicode/XML Leningrad Codex）`
        : currentType === 'greek'
          ? `${bookNameJa} 全${GREEK_EDITIONS[section].chapters}章 ギリシャ語原文（Robinson–Pierpont 2018）`
          : currentType === 'latin'
            ? `${bookNameJa} 全${LATIN_EDITIONS[section].chapters}章 ラテン語訳（クレメンス版ウルガタ聖書、Hetzenauer 1914）`
            : currentType === 'zh'
              ? `${bookNameJa} 全${CHINESE_EDITIONS[section].chapters}章 中文（新标点和合本・简体字）`
              : '口語訳聖書 旧約：1955年版・新約：1954年版',
      url: `https://www.asmrchurch.com/bible/${section}?type=${currentType}`,
      image: 'https://www.asmrchurch.com/static/images/card1.jpg'
    });
  }, [content, section, chapter, verse, searchParams, bookNamesJa, hasScrolledToVerse, currentType, isSocial]);

  // Add click handlers for verse sharing
  useEffect(() => {
    if (!content || isSocial) return;

    const handleVerseClick = (e) => {
      // Check if clicked element is a verse link
      const link = e.target.closest('em[id] a');
      if (!link) return;

      e.preventDefault();
      const verseId = link.parentElement.id; // e.g., "1-1"
      const [chapterNum, verseNum] = verseId.split('-');

      // Get the verse text
      const verseElement = link.parentElement.nextElementSibling;
      if (!verseElement) return;

      // Get the innermost span text (nested spans with id='verse')
      const verseSpans = verseElement.querySelectorAll('span[id="verse"]');
      const verseText = verseSpans.length > 0
        ? readingText(verseSpans[verseSpans.length - 1])
        : readingText(verseElement);

      // Get book name in Japanese
      const bookNameJa = bookNamesJa[section] || section;

      // Construct share text with new URL format
      const shareTitle = `${bookNameJa} ${chapterNum}:${verseNum}`;
      const shareText = `${verseText} - ${shareTitle}`;
      const shareUrl = `${window.location.origin}/bible/${section}/${chapterNum}/${verseNum}?type=${currentType}`;

      // Get click position for popup
      const rect = link.getBoundingClientRect();

      // Show share popup with X and Facebook buttons
      setSharePopup({
        show: true,
        title: shareTitle,
        text: shareText,
        url: shareUrl,
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY + 5,
        preview: '' // No preview for single verse click
      });
    };

    // Handle right-click on selected verses
    const handleContextMenu = (e) => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) return;

      const selectedText = selection.toString().trim();
      if (!selectedText) return;

      // Find all verse number elements (em tags with id) in the selected area
      const allEmElements = document.querySelectorAll('em[id]');
      const selectedVerses = [];

      allEmElements.forEach(emElement => {
        if (selection.containsNode(emElement, true) ||
            selection.containsNode(emElement.nextElementSibling, true)) {
          const verseId = emElement.id;
          const [chap, ver] = verseId.split('-');
          const verseTextElement = emElement.nextElementSibling;

          if (verseTextElement) {
            const verseSpans = verseTextElement.querySelectorAll('span[id="verse"]');
            const verseText = verseSpans.length > 0
              ? readingText(verseSpans[verseSpans.length - 1])
              : readingText(verseTextElement);

            selectedVerses.push({
              chapter: chap,
              verse: ver,
              text: verseText
            });
          }
        }
      });

      if (selectedVerses.length > 0) {
        e.preventDefault();

        // Get book name in Japanese
        const bookNameJa = bookNamesJa[section] || section;

        // Construct combined text
        const firstVerse = selectedVerses[0];
        const lastVerse = selectedVerses[selectedVerses.length - 1];

        let shareTitle, shareUrl;
        if (selectedVerses.length === 1) {
          shareTitle = `${bookNameJa} ${firstVerse.chapter}:${firstVerse.verse}`;
          shareUrl = `${window.location.origin}/bible/${section}/${firstVerse.chapter}/${firstVerse.verse}?type=${currentType}`;
        } else {
          shareTitle = `${bookNameJa} ${firstVerse.chapter}:${firstVerse.verse}-${lastVerse.verse}`;
          shareUrl = `${window.location.origin}/bible/${section}/${firstVerse.chapter}/${firstVerse.verse}?type=${currentType}&end=${lastVerse.verse}`;
        }

        // Combine all verse texts
        const combinedText = selectedVerses.map((v, i) =>
          `${v.text}${i < selectedVerses.length - 1 ? ' ' : ''}`
        ).join('');

        const shareText = `${combinedText} - ${shareTitle}`;

        // Show share popup at right-click position with preview
        setSharePopup({
          show: true,
          title: shareTitle,
          text: shareText,
          url: shareUrl,
          x: e.pageX,
          y: e.pageY,
          preview: combinedText // Show preview of selected verses
        });
      }
    };

    // Add event listener to content div
    const contentDiv = document.querySelector('.content section');
    if (contentDiv) {
      contentDiv.addEventListener('click', handleVerseClick);
      contentDiv.addEventListener('contextmenu', handleContextMenu);
    }

    // Cleanup
    return () => {
      if (contentDiv) {
        contentDiv.removeEventListener('click', handleVerseClick);
        contentDiv.removeEventListener('contextmenu', handleContextMenu);
      }
    };
  }, [content, section, bookNamesJa, currentType, isSocial]);

  return (
    <div style={currentType === 'zh' ? { '--chinese-landscape-image': `url("${process.env.PUBLIC_URL}/static/images/backgrounds/chinese-jiangnan-mountains.webp")` } : undefined} data-testament={testament} className={`bibleframe ${currentType === 'zh' ? 'chinese-theme' : ''} ${isGalEdition && !isSocial ? 'bibleframe--gal' : ''} ${isSocial ? 'bibleframe--social' : ''} commentary-none`}>
      {currentType === 'zh' && <SpaceBackground chinese />}
      {reformationVerse && <ReformationCommentary
        key={`${reformationVerse.book || section}-${reformationVerse.chapter}-${reformationVerse.verse}`}
        book={section} bookName={currentType === 'en' ? (content.match(/<h1[^>]*>(.*?)<\/h1>/i)?.[1]?.replace(/<[^>]+>/g, '') || section) : (bookNamesJa[section] || section)} {...reformationVerse}
        language={currentType === 'en' ? 'en' : 'ja'}
        onClose={() => setReformationVerse(null)}
      />}
      <Header
        bible={true}
        galEdition={isGalEdition && !isSocial}
        subon={true}
        title={metaData.title}
        description={metaData.description}
        url={metaData.url}
        image={metaData.image}
      />
      {!isSocial && !isGalEdition && <ChapterNavigation
        htmlContent={content}
        currentChapter={currentChapter}
        onChapterClick={setCurrentChapter}
      />}
      <div className="flex-container">
        <div className="left-area">
          <AdIcons />
        </div>

        <div className="content">
          {!isSocial && isGalEdition && (
            <div className="gal-intro">
              <span className="gal-intro__label">{galEdition.label} / GAL EDITION</span>
              <span className="gal-intro__sparkles" aria-hidden="true">✦ ♡ ✧</span>
              <p>聖書、うちらの言葉で。</p>
              <span className="gal-intro__caption">{galEdition.name}をギャルの言葉で。{galEdition.headline}</span>
            </div>
          )}
          {!isSocial && isGalEdition && (
            <div className="gal-reading-navigation">
              <div className="gal-reading-navigation__label">READ THE BIBLE <span aria-hidden="true">♡</span></div>
              <ChapterNavigation
                htmlContent={content}
                currentChapter={currentChapter}
                onChapterClick={setCurrentChapter}
              />
            </div>
          )}
          {!isSocial && !showComingSoon && supportsReformationCommentary && <p className="reformation-hint">{currentType === 'en' ? 'Double-click a verse or select its book icon to read commentary by John Calvin and Martin Luther.' : '本文をダブルクリック、または各節の本のアイコンから、カルバン・ルターの解説を読めます。'}</p>}
          {isSocial ? <BibleSocial onCommentary={setReformationVerse} /> : <section onDoubleClick={openReformationCommentary} onClick={openReformationCommentary}>
            {showComingSoon ? (
              <ComingSoon bookName={section} />
            ) : (
              <div
                ref={readingContainerRef}
                className={['norm', 'ruby', 'hebrew', 'greek', 'latin', 'zh'].includes(currentType) ? `bible-reading bible-reading--${currentType}` : ''}
                dangerouslySetInnerHTML={{ __html: readingContent }}
              />
            )}
          </section>}
          <div className="mobile-ads">
            <AdIcons />
          </div>
        </div>

        <div className="right-area booth-product-sidebar">
          <SienBanner />
          <BoothProduct />
        </div>
      </div>

      {/* Share Popup */}
      {sharePopup.show && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setSharePopup({ ...sharePopup, show: false })}
          />
          <div
            style={{
              position: 'absolute',
              left: sharePopup.x,
              top: sharePopup.y,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1000,
              maxWidth: '500px'
            }}
          >
            {sharePopup.preview && (
              <div
                style={{
                  backgroundColor: '#f9f9f9',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  borderLeft: '3px solid #e99708'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>
                  {sharePopup.title}
                </div>
                <div style={{ color: '#555' }}>
                  {sharePopup.preview}
                </div>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}
            >
              <span style={{ fontSize: '14px', marginRight: '8px' }}>布教する:</span>
              <TwitterShareButton url={sharePopup.url} title={sharePopup.text}>
                <XIcon size={32} round={false} />
              </TwitterShareButton>
              <FacebookShareButton url={sharePopup.url} quote={sharePopup.text}>
                <FacebookIcon size={32} round={false} />
              </FacebookShareButton>
              <EmailShareButton url={sharePopup.url} subject={sharePopup.title} body={sharePopup.text}>
                <EmailIcon size={32} round={false} />
              </EmailShareButton>
              <RedditShareButton url={sharePopup.url} title={sharePopup.text}>
                <RedditIcon size={32} round={false} />
              </RedditShareButton>
              <LinkedinShareButton url={sharePopup.url} title={sharePopup.title} summary={sharePopup.text}>
                <LinkedinIcon size={32} round={false} />
              </LinkedinShareButton>
              <BloggerShareButton
                url={sharePopup.url}
                title={sharePopup.title}
                text={sharePopup.preview || sharePopup.text}
                image="https://www.asmrchurch.com/static/images/c4.jpg"
              >
                <BloggerIcon size={32} />
              </BloggerShareButton>
            </div>
          </div>
        </>
      )}

      <Footer />

      {/* Verse highlight styles */}
      <style>{`
        .verse-highlight {
          background-color: rgba(255, 255, 0, 0.3);
          padding: 2px 4px;
          border-radius: 3px;
          transition: background-color 0.3s ease;
        }
      `}</style>
    </div>
  );
}

export default Bible;
